import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, CheckCircle, DeleteOutline } from "@mui/icons-material";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../services/firebase";

interface UserFeedback {
  id: string;
  content: string;
  timestamp: { _seconds: number };
  createdAt: { _seconds: number };
  status: "pending" | "read" | "resolved";
  platform: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  appVersion: string | null;
}

const UserFeedbackList: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<UserFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedback | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserFeedback | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      // Firestore から直接取得
      const q = query(
        collection(db, "customerFeedbacks"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const feedbackList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const createdAt = data.createdAt as Timestamp | null;
        const timestamp = data.timestamp as Timestamp | null;
        return {
          id: docSnap.id,
          content: data.content,
          status: data.status || "pending",
          createdAt: createdAt
            ? { _seconds: createdAt.seconds }
            : { _seconds: Math.floor(Date.now() / 1000) },
          timestamp: timestamp
            ? { _seconds: timestamp.seconds }
            : { _seconds: Math.floor(Date.now() / 1000) },
          platform: data.platform || null,
          osVersion: data.osVersion || null,
          deviceModel: data.deviceModel || null,
          appVersion: data.appVersion || null,
        } as UserFeedback;
      });
      setFeedbacks(feedbackList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const handleOpenDialog = (feedback: UserFeedback) => {
    setSelectedFeedback(feedback);
    setDialogOpen(true);

    // ダイアログを開いたら既読にする
    if (feedback.status === "pending") {
      updateFeedbackStatus(feedback.id, "read");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFeedback(null);
  };

  const updateFeedbackStatus = async (feedbackId: string, status: "read" | "resolved") => {
    try {
      // Firestore に直接更新
      const docRef = doc(db, "customerFeedbacks", feedbackId);
      await updateDoc(docRef, {
        status,
        updatedAt: serverTimestamp(),
      });

      // ローカルステートを更新
      setFeedbacks((prev) =>
        prev.map((feedback) => (feedback.id === feedbackId ? { ...feedback, status } : feedback))
      );

      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status });
      }
    } catch (err) {
      console.error("Error updating feedback status:", err);
      alert("ステータスの更新に失敗しました");
    }
  };

  const handleMarkAsResolved = async () => {
    if (selectedFeedback) {
      await updateFeedbackStatus(selectedFeedback.id, "resolved");
      handleCloseDialog();
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, "customerFeedbacks", deleteTarget.id));
      setFeedbacks((prev) => prev.filter((feedback) => feedback.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Error deleting feedback:", err);
      alert("削除に失敗しました");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "未読";
      case "read":
        return "既読";
      case "resolved":
        return "対応済み";
      default:
        return status;
    }
  };

  const getStatusColor = (
    status: string
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case "pending":
        return "error";
      case "read":
        return "warning";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            ご意見・ご要望
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            アプリからのご意見・ご要望の一覧です
          </Typography>

          <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ステータス</TableCell>
                  <TableCell>投稿日時</TableCell>
                  <TableCell>端末</TableCell>
                  <TableCell>内容（プレビュー）</TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedbacks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      フィードバックはありません
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbacks.map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(feedback.status)}
                          color={getStatusColor(feedback.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(feedback.createdAt._seconds * 1000).toLocaleString("ja-JP")}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {feedback.deviceModel || "-"}
                        </Typography>
                        {feedback.platform && (
                          <Typography variant="caption" color="text.secondary">
                            {feedback.platform} {feedback.osVersion || ""}
                            {feedback.appVersion ? ` / v${feedback.appVersion}` : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {feedback.content.length > 50
                          ? `${feedback.content.substring(0, 50)}...`
                          : feedback.content}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog(feedback)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setDeleteTarget(feedback)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 詳細ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          フィードバック詳細
          {selectedFeedback && (
            <Chip
              label={getStatusLabel(selectedFeedback.status)}
              color={getStatusColor(selectedFeedback.status)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                投稿日時
              </Typography>
              <Typography variant="body1" paragraph>
                {new Date(selectedFeedback.createdAt._seconds * 1000).toLocaleString("ja-JP")}
              </Typography>

              {selectedFeedback.platform && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    端末情報
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedFeedback.deviceModel || "不明"} / {selectedFeedback.platform}{" "}
                    {selectedFeedback.osVersion || ""} / アプリ v
                    {selectedFeedback.appVersion || "不明"}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                内容
              </Typography>
              <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedFeedback.content}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedFeedback && selectedFeedback.status !== "resolved" && (
            <Button
              startIcon={<CheckCircle />}
              variant="contained"
              color="success"
              onClick={handleMarkAsResolved}
            >
              対応済みにする
            </Button>
          )}
          <Button onClick={handleCloseDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>ご意見・ご要望を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            この操作は取り消せません。イタズラなど不要な投稿を完全に削除します。
          </Typography>
          {deleteTarget && (
            <Paper sx={{ p: 2, bgcolor: "grey.50", mt: 2 }}>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {deleteTarget.content.length > 200
                  ? `${deleteTarget.content.substring(0, 200)}...`
                  : deleteTarget.content}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserFeedbackList;
